import { usePageTitle } from "@/hooks/use-page-title";
import { parseDateOnly } from "@/lib/dateUtils";
import { useEffect } from "react";
import type { WorkOrderWithRelations } from "@shared/schema";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, Upload, Download, CheckCircle, Clock } from "lucide-react";

export default function Documents() {
  usePageTitle("Documents");
  const { toast } = useToast();
  const { isAuthenticated, isLoading } = useAuth();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, isLoading, toast]);

  const { data: workOrders, isLoading: workOrdersLoading } = useQuery<WorkOrderWithRelations[]>({
    queryKey: ["/api/work-orders"],
    retry: false,
    refetchOnMount: "always",
  });

  if (isLoading || !isAuthenticated) {
    return <div className="min-h-screen bg-slate-50" />;
  }

  // Extract all document requests from work orders
  const documentRequests = workOrders?.flatMap((order: any) => 
    order.documentRequests?.map((doc: any) => ({
      ...doc,
      workOrder: order
    })) || []
  ) || [];

  const getDocumentStatusColor = (submitted: boolean) => {
    return submitted ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800";
  };

  const formatServiceType = (serviceType: string) => {
    return serviceType.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <ResponsiveLayout title="Documents" subtitle="Track document requests and submissions">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Document Requests</h2>
        <p className="text-sm text-gray-600">Track document requests and submissions</p>
      </div>
          
          {workOrdersLoading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading documents...</p>
            </div>
          ) : documentRequests.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No document requests found</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {documentRequests.map((docRequest: any) => (
                <Card key={docRequest.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          {docRequest.submitted ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <Clock className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-gray-900">{docRequest.documentType}</h3>
                          <p className="text-xs text-gray-500">
                            WO: {docRequest.workOrder.workOrderNumber}
                          </p>
                        </div>
                      </div>
                      <Badge className={getDocumentStatusColor(docRequest.submitted)}>
                        {docRequest.submitted ? "Submitted" : "Pending"}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Carrier:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {docRequest.workOrder.carrier.name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Service:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {formatServiceType(docRequest.workOrder.serviceType)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Required:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {docRequest.required ? "Yes" : "No"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Requested:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {docRequest.requestedDate ? parseDateOnly(docRequest.requestedDate).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                      {docRequest.submitted && docRequest.submittedDate && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Submitted:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {parseDateOnly(docRequest.submittedDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {docRequest.description && (
                      <div className="mb-4">
                        <p className="text-sm text-gray-600">{docRequest.description}</p>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {!docRequest.submitted && (
                          <Button size="sm" variant="outline">
                            <Upload className="w-4 h-4 mr-1" />
                            Upload
                          </Button>
                        )}
                        {docRequest.submitted && (
                          <Button size="sm" variant="outline">
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                        )}
                      </div>
                      <Button variant="link" size="sm">
                        View Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
    </ResponsiveLayout>
  );
}
